import React, {ReactNode} from "react";
import {datadogRum} from "@datadog/browser-rum-slim";
import {Script} from "gatsby";

// global style, it's very IMPORTANT that this should load first
// before loading any other component
import "./src/styles/common.scss";

import {initSentry, logErrorToSentry} from "./src/utils/sentry";
import {updateUtmCookies} from "./src/utils/UTM-tracking";
import {logEvent} from "./src/utils/amplitude";
import ErrorBoundary from "./src/components/error-boundary/ErrorBoundary";
import GDPRWrapper from "./src/components/gdpr/wrapper/GDPRWrapper";
import FlexportAsciiShip from "./src/constants/flexport-ascii-ship";
import {logPath} from "./src/utils/path-logging";
import WindowSizeProvider from "./src/contexts/window-context";
import {AlgoliaContext} from "./src/contexts/context";
import {checkMsclkid} from "./src/utils/microsoft-click-id";
import {logDataLayerEvent} from "./src/utils/gtm-data-layer";

// inert polyfill
import "wicg-inert";

import {
  GATSBY_DATADOG_CLIENT_TOKEN,
  GATSBY_DATADOG_APPLICATION_ID,
  NODE_ENV,
  GATSBY_RECAPTCHA_SITE_KEY,
} from "./src/config";

export const wrapRootElement = ({element}: {element: ReactNode}) => {
  return (
    <ErrorBoundary>
      <GDPRWrapper>
        <WindowSizeProvider>{element}</WindowSizeProvider>
      </GDPRWrapper>
    </ErrorBoundary>
  );
};

export const wrapPageElement = ({
  element,
  props,
}: {
  element: ReactNode;
  props: any;
}) => {
  return (
    <>
      <Script
        id="fp-grecaptcha"
        src={`https://www.recaptcha.net/recaptcha/enterprise.js?render=${GATSBY_RECAPTCHA_SITE_KEY}`}
        strategy="idle"
      />
      {/*
        passing along algolia context here
        grab it from the page context
        this way, we don't need to create new env prefixed with GATSBY_
      */}
      <AlgoliaContext.Provider value={props.pageContext.algoliaIndex!}>
        {element}
      </AlgoliaContext.Provider>
    </>
  );
};

let gtmEventTimeoutId: NodeJS.Timeout | null = null;
export const onRouteUpdate = ({location}: {location: Location}) => {
  logEvent("gatsbyRouteUpdate", {
    path: location.pathname,
  });
  logPath(location.pathname);

  // Let GTM know the route has updated after delay
  if (gtmEventTimeoutId) {
    clearTimeout(gtmEventTimeoutId);
  }

  gtmEventTimeoutId = setTimeout(() => {
    try {
      logDataLayerEvent("gatsbyRouteLoaded", {}, NODE_ENV != "production");
    } catch (error) {
    } finally {
      // Reset the ID after execution
      gtmEventTimeoutId = null;
    }
  }, 300);

  // check and store microsoft click id
  checkMsclkid();
};

export const onInitialClientRender = () => {
  if (NODE_ENV === "production") {
    // https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/#filter-out-bot-sessions-on-intake
    const botPattern =
      "(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";

    const regex = new RegExp(botPattern, "i");

    // define var conditionalSampleRate as 0 if the userAgent matches a pattern in botPatterns
    // otherwise, define conditionalSampleRate as 100
    const conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : 100;
    datadogRum.init({
      applicationId: GATSBY_DATADOG_APPLICATION_ID!,
      clientToken: GATSBY_DATADOG_CLIENT_TOKEN!,
      site: "datadoghq.com",
      service: "flexport-static-site",
      env: "production",
      sampleRate: 100,
      trackInteractions: true,
      defaultPrivacyLevel: "mask-user-input",
      sessionSampleRate: conditionalSampleRate,
    });
    datadogRum.startSessionReplayRecording();
  }

  initSentry();

  try {
    /*
      All scripts should be put inside this try-catch block, so we can track all
      errors in the sentry
    */

    // Suppress ZoomInfo blob URL syntax errors that occur when their endpoint returns HTML
    // https://flexport.sentry.io/issues/1628445243/events/?project=1847116
    const isZoomInfoBlobError = (error: any, source?: string) => {
      // Match blob URLs from current origin with SyntaxError "Unexpected token '<'"
      const escapedOrigin = window.location.origin.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      const blobPattern = `^blob:${escapedOrigin}\/[a-f0-9-]+$`;
      return (
        error instanceof SyntaxError &&
        error.message?.includes("Unexpected token '<'") &&
        source?.match(new RegExp(blobPattern, "i"))
      );
    };

    window.addEventListener("error", event => {
      if (isZoomInfoBlobError(event.error, event.filename)) {
        event.preventDefault();
        console.warn("Suppressed ZoomInfo blob error:", event.message);
      }
    });

    window.addEventListener("unhandledrejection", event => {
      if (isZoomInfoBlobError(event.reason, event.reason?.stack)) {
        event.preventDefault();
        console.warn(
          "Suppressed ZoomInfo promise rejection:",
          event.reason.message
        );
      }
    });

    updateUtmCookies();

    if (process.env.NODE_ENV === "production") {
      /* eslint-disable-next-line no-console */
      console.log(FlexportAsciiShip);
    }
  } catch (error) {
    logErrorToSentry(error);
  }
};

// export const replaceHydrateFunction = () => {
//   return (element, container) => {
//     const root = ReactDOM.createRoot(container);
//     root.render(element);
//   };
// };

export const shouldUpdateScroll = ({
  routerProps: {location},
  getSavedScrollPosition,
}) => {
  if (location.state && location.state.preventScrollReset) {
    // tell Gatsby NOT to manage scrolling for this navigation
    return false;
  }

  // For all other navigation events, use Gatsby's default behavior
  const savedPosition = getSavedScrollPosition(location, location.key);
  return savedPosition || [0, 0];
};
